import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/* Modalità Cartacea — vista tipografica raggruppata per sezioni personalizzabili.
 *
 * Sezioni (PaperSection): fetched from /api/paper-sections. Il matching è:
 *   - per ogni vino, la PRIMA sezione (in ordine `order` crescente) i cui
 *     criteri matchano il vino vince. Un vino appare in una sola sezione.
 *   - i vini non catturati da alcuna sezione con criteri specifici cadono
 *     nella sezione `catch_all=true` (se presente e attiva).
 *
 * Criteri di una sezione (AND):
 *   - tipologie: ["rosso", "bianco", ...]  (empty = qualunque)
 *   - paesi:     ["Italia", "Francia"]     (empty = qualunque)
 *   - zone:      ["Piemonte", ...]         (empty = qualunque)  — match su wine.region
 *   - categorie: ["vino", "distillato"]    (empty = qualunque)
 *
 * Se il fetch delle sezioni fallisce, fallback: raggruppa per tipologia
 * (compat con il vecchio comportamento).
 *
 * BUG-FIX Fase 6.5: produttore sempre visibile, in corsivo sotto il nome.
 */

const FALLBACK_TYPE_TITLES = {
    bollicine: "Bollicine",
    bianco: "Vini Bianchi",
    rosato: "Vini Rosati",
    rosso: "Vini Rossi",
    dolce: "Vini da Meditazione & Dolci",
};
const FALLBACK_TYPE_ORDER = ["bollicine", "bianco", "rosato", "rosso", "dolce"];

function lc(s) { return (s || "").toString().toLowerCase().trim(); }

function wineMatchesSection(wine, section) {
    const crit = section.criteria || {};
    // categoria
    const cats = (crit.categorie || []).map(lc);
    if (cats.length > 0 && !cats.includes(lc(wine.categoria || "vino"))) return false;
    // tipologia
    const tips = (crit.tipologie || []).map(lc);
    if (tips.length > 0 && !tips.includes(lc(wine.type))) return false;
    // paese
    const paesi = (crit.paesi || []).map(lc);
    if (paesi.length > 0 && !paesi.includes(lc(wine.country))) return false;
    // zona (matches wine.region)
    const zone = (crit.zone || []).map(lc);
    if (zone.length > 0 && !zone.includes(lc(wine.region))) return false;
    return true;
}

function priceRangeLabel(wine) {
    const prices = (wine.formats || [])
        .map((f) => (typeof f.price === "number" ? f.price : null))
        // Filter out zero/negative prices — never surface "€ 0" on the menu.
        .filter((p) => p !== null && p > 0);
    if (prices.length === 0) return null;
    const min = Math.min(...prices);
    if (prices.length === 1) return min.toFixed(2);
    const max = Math.max(...prices);
    if (min === max) return min.toFixed(2);
    return `${min.toFixed(2)}–${max.toFixed(2)}`;
}
function formatSizes(wine, showSku = false) {
    return (wine.formats || [])
        .map((f) => {
            const sku = showSku && f.mexal_sku ? ` (${f.mexal_sku})` : "";
            return `${f.size} L${sku}`;
        })
        .join(" · ");
}

export default function PaperListView({ wines, canSeePrices, noticeForNoPrice, onWineClick, sections: sectionsProp, showSku = false }) {
    // Sections: use prop if provided (admin print page can pass them), else fetch
    const [sections, setSections] = useState(sectionsProp || null);
    useEffect(() => {
        if (sectionsProp) { setSections(sectionsProp); return; }
        api.get("/paper-sections")
            .then(({ data }) => setSections(data.items || []))
            .catch(() => setSections([]));
    }, [sectionsProp]);

    // Dedupe + build sub-groups per section, keeping ORDER of sections.
    const layout = useMemo(() => {
        if (!sections) return null;
        // Dedupe input wines
        const seen = new Set();
        const uniq = [];
        for (const w of wines) {
            if (seen.has(w.id)) continue;
            seen.add(w.id);
            uniq.push(w);
        }
        // Section list ordered
        const ordered = [...sections].filter((s) => s.active !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        const catchAllIdx = ordered.findIndex((s) => s.catch_all);
        // Assign each wine to the first matching section (ignoring catch_all first).
        const buckets = ordered.map(() => []);
        for (const w of uniq) {
            let placed = false;
            for (let i = 0; i < ordered.length; i++) {
                if (i === catchAllIdx) continue; // skip catch-all
                if (wineMatchesSection(w, ordered[i])) {
                    buckets[i].push(w);
                    placed = true;
                    break;
                }
            }
            if (!placed && catchAllIdx >= 0) {
                buckets[catchAllIdx].push(w);
            }
        }
        // For each section, sub-group by zone (country != Italia → country, else region)
        return ordered.map((sec, i) => {
            const items = buckets[i];
            const byZone = {};
            for (const w of items) {
                const zoneKey = w.country && lc(w.country) !== "italia"
                    ? w.country
                    : (w.region || "Altre zone");
                if (!byZone[zoneKey]) byZone[zoneKey] = [];
                byZone[zoneKey].push(w);
            }
            // Sort wines within each zone: producer → name
            for (const z of Object.keys(byZone)) {
                byZone[z].sort((a, b) => {
                    const pa = lc(a.producer);
                    const pb = lc(b.producer);
                    if (pa !== pb) return pa.localeCompare(pb);
                    return (a.name || "").localeCompare(b.name || "");
                });
            }
            return { section: sec, byZone, count: items.length };
        });
    }, [wines, sections]);

    // Fallback layout (only if sections fetch failed / no sections found)
    const fallbackLayout = useMemo(() => {
        if (!sections || sections.length > 0) return null;
        const byType = {};
        const seen = new Set();
        for (const w of wines) {
            if (seen.has(w.id)) continue;
            seen.add(w.id);
            const t = w.type || "rosso";
            const zk = w.country && lc(w.country) !== "italia" ? w.country : (w.region || "Altre zone");
            if (!byType[t]) byType[t] = {};
            if (!byType[t][zk]) byType[t][zk] = [];
            byType[t][zk].push(w);
        }
        const order = [...FALLBACK_TYPE_ORDER, ...Object.keys(byType).filter((t) => !FALLBACK_TYPE_ORDER.includes(t))];
        return order.filter((t) => byType[t]).map((t) => ({
            section: { id: `fallback-${t}`, title: FALLBACK_TYPE_TITLES[t] || t,
                        order: 0, catch_all: false, criteria: {} },
            byZone: byType[t],
            count: Object.values(byType[t]).flat().length,
        }));
    }, [wines, sections]);

    const finalLayout = layout && layout.length > 0 ? layout : fallbackLayout;

    if (!finalLayout) {
        return <div className="py-8 text-center co-caption">Caricamento sezioni…</div>;
    }
    if (wines.length === 0) {
        return (
            <div className="py-16 text-center text-[hsl(var(--muted-foreground))] italic" data-testid="paper-list-empty">
                Nessun vino corrisponde alla ricerca.
            </div>
        );
    }

    return (
        <div className="paper-list max-w-3xl mx-auto pb-16" data-testid="paper-list-view">
            {finalLayout.map(({ section, byZone, count }) => {
                if (count === 0) return null; // hide empty sections
                const zoneKeys = Object.keys(byZone).sort();
                return (
                    <section key={section.id}
                        className="paper-list-type mt-10 first:mt-4"
                        data-testid={`paper-list-section-${section.id}`}>
                        <h2 className="paper-list-h2 text-center text-2xl uppercase tracking-[0.2em] pb-2 border-b border-[hsl(var(--border))] mb-6"
                            data-testid={`paper-list-section-title-${section.id}`}>
                            {section.title}
                        </h2>
                        {zoneKeys.map((zk) => {
                            const items = byZone[zk];
                            return (
                                <div key={zk} className="paper-list-zone mb-6" data-testid={`paper-list-zone-${zk}`}>
                                    <h3 className="paper-list-h3 text-sm tracking-[0.15em] uppercase text-[hsl(var(--muted-foreground))] mb-2 border-b border-[hsl(var(--border))]/60 pb-1">
                                        {zk}
                                    </h3>
                                    <ul className="divide-y divide-[hsl(var(--border))]/40">
                                        {items.map((w) => {
                                            const price = priceRangeLabel(w);
                                            const sizes = formatSizes(w, showSku);
                                            const producerVisible = w.producer && w.producer !== "—" && w.producer.trim() !== "";
                                            return (
                                                <li key={`${section.id}-${zk}-${w.id}`}
                                                    onClick={() => onWineClick?.(w.id)}
                                                    className="paper-list-row py-3 grid grid-cols-[1fr_auto] gap-3 items-baseline hover:bg-black/[0.02] cursor-pointer"
                                                    data-testid={`paper-list-row-${w.id}`}>
                                                    <div className="min-w-0">
                                                        <div className="flex items-baseline gap-2 flex-wrap">
                                                            <span className="paper-list-name text-lg leading-snug"
                                                                data-testid={`paper-list-row-name-${w.id}`}>
                                                                {w.name}
                                                            </span>
                                                            {w.vintage && (
                                                                <span className="text-[hsl(var(--muted-foreground))] italic text-sm"
                                                                    data-testid={`paper-list-row-vintage-${w.id}`}>
                                                                    {w.vintage}
                                                                </span>
                                                            )}
                                                            {w.glass_available && (
                                                                <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] print:hidden">
                                                                    · al calice
                                                                </span>
                                                            )}
                                                        </div>
                                                        {producerVisible && (
                                                            <div className="paper-list-producer italic text-[hsl(var(--foreground))]/85 text-base leading-tight mt-0.5"
                                                                data-testid={`paper-list-row-producer-${w.id}`}>
                                                                {w.producer}
                                                            </div>
                                                        )}
                                                        {sizes && (
                                                            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5"
                                                                data-testid={`paper-list-row-sizes-${w.id}`}>
                                                                {sizes}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="text-right whitespace-nowrap tabular-nums text-base"
                                                        data-testid={`paper-list-row-price-${w.id}`}>
                                                        {price ? `€ ${price}` : (
                                                            <span className="text-xs italic text-[hsl(var(--muted-foreground))] print:hidden">
                                                                {noticeForNoPrice || "—"}
                                                            </span>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        })}
                    </section>
                );
            })}
        </div>
    );
}
